import { UserAccountStatus, UserRole } from '../gen/proto/api/gateway/v1/user_pb';
import { SiphonDiameter, SiphonMaterial, SiphonType } from '../gen/proto/api/gateway/v1/installation_pb';
import { InstallationState, InstallationStateType } from '../gen/proto/api/gateway/v1/installation_state_pb';
import { DeviceSensorType, DeviceSensorUltrasonicModel } from '../gen/proto/api/gateway/v1/device_pb';
import { DeviceTrackerType } from '../gen/proto/api/gateway/v1/device_tracker_pb';
import i18next from '../i18n/i18n';

export const userRoleLabel = (role: UserRole): string => {
  const labels: Record<UserRole, string> = {
    [UserRole.UNSPECIFIED]: i18next.t('account:enums.role.unspecified'),
    [UserRole.SUPER_ADMIN]: i18next.t('account:enums.role.superAdmin'),
    [UserRole.COMPANY_ADMIN]: i18next.t('account:enums.role.companyAdmin'),
    [UserRole.ADMIN]: i18next.t('account:enums.role.admin'),
    [UserRole.USER]: i18next.t('account:enums.role.user'),
  };
  return labels[role];
};

export const userAccountStatusLabel = (status: UserAccountStatus): string => {
  const labels: Record<UserAccountStatus, string> = {
    [UserAccountStatus.UNSPECIFIED]: i18next.t('account:enums.accountStatus.unspecified'),
    [UserAccountStatus.ACTIVE]: i18next.t('account:enums.accountStatus.active'),
    [UserAccountStatus.BLOCKED]: i18next.t('account:enums.accountStatus.blocked'),
  };
  return labels[status];
};

const SIPHON_PREFIX = 'installation-location:installationLocationDetail.installation.summary.siphon';

export const siphonTypeLabel = (type: SiphonType): string => {
  const labels: Record<SiphonType, string> = {
    [SiphonType.EFFLUENT]: i18next.t(`${SIPHON_PREFIX}.types.effluent`),
    [SiphonType.INFLUENT]: i18next.t(`${SIPHON_PREFIX}.types.influent`),
    [SiphonType.UNSPECIFIED]: i18next.t(`${SIPHON_PREFIX}.types.unspecified`),
  };
  return labels[type] ?? i18next.t(`${SIPHON_PREFIX}.types.unspecified`);
};

export const siphonMaterialLabel = (material: SiphonMaterial): string => {
  const labels: Record<SiphonMaterial, string> = {
    [SiphonMaterial.PVC]: i18next.t(`${SIPHON_PREFIX}.materials.pvc`),
    [SiphonMaterial.HDPE]: i18next.t(`${SIPHON_PREFIX}.materials.hdpe`),
    [SiphonMaterial.OTHER]: i18next.t(`${SIPHON_PREFIX}.materials.other`),
    [SiphonMaterial.UNSPECIFIED]: i18next.t(`${SIPHON_PREFIX}.materials.unspecified`),
  };
  return labels[material] ?? i18next.t(`${SIPHON_PREFIX}.materials.unspecified`);
};

export const siphonDiameterLabel = (diameter: SiphonDiameter): string => {
  const labels: Record<SiphonDiameter, string> = {
    [SiphonDiameter.SIPHON_DIAMETER_90_MM]: i18next.t(`${SIPHON_PREFIX}.diameters.90mm`),
    [SiphonDiameter.SIPHON_DIAMETER_110_MM]: i18next.t(`${SIPHON_PREFIX}.diameters.110mm`),
    [SiphonDiameter.SIPHON_DIAMETER_160_MM]: i18next.t(`${SIPHON_PREFIX}.diameters.160mm`),
    [SiphonDiameter.SIPHON_DIAMETER_200_MM]: i18next.t(`${SIPHON_PREFIX}.diameters.200mm`),
    [SiphonDiameter.SIPHON_DIAMETER_240_MM]: i18next.t(`${SIPHON_PREFIX}.diameters.240mm`),
    [SiphonDiameter.SIPHON_DIAMETER_OTHER]: i18next.t(`${SIPHON_PREFIX}.diameters.other`),
    [SiphonDiameter.SIPHON_DIAMETER_UNSPECIFIED]: i18next.t(`${SIPHON_PREFIX}.diameters.unspecified`),
  };
  return labels[diameter] ?? i18next.t(`${SIPHON_PREFIX}.diameters.unspecified`);
};

const DEVICE_PREFIX = 'installation-location:installationLocationDetail.installation.summary.devices';

export const deviceSensorUltrasonicModelLabel = (model: DeviceSensorUltrasonicModel): string => {
  const labels: Record<DeviceSensorUltrasonicModel, string> = {
    [DeviceSensorUltrasonicModel.US100]: i18next.t(`${DEVICE_PREFIX}.ultrasonicModels.us100`),
    [DeviceSensorUltrasonicModel.UNSPECIFIED]: i18next.t(`${DEVICE_PREFIX}.ultrasonicModels.unspecified`),
  };
  return labels[model] ?? i18next.t(`${DEVICE_PREFIX}.ultrasonicModels.unspecified`);
};

export const deviceSensorTypeLabel = (type: DeviceSensorType): string => {
  const labels: Record<DeviceSensorType, string> = {
    [DeviceSensorType.ULTRASONIC]: i18next.t(`${DEVICE_PREFIX}.sensorTypes.ultrasonic`),
    [DeviceSensorType.UNSPECIFIED]: i18next.t(`${DEVICE_PREFIX}.sensorTypes.unspecified`),
  };
  return labels[type] ?? i18next.t(`${DEVICE_PREFIX}.sensorTypes.unspecified`);
};

export const deviceLocationTypeLabel = (type: DeviceTrackerType): string => {
  const labels: Record<DeviceTrackerType, string> = {
    [DeviceTrackerType.UNSPECIFIED]: i18next.t('devices:locationType.UNSPECIFIED'),
    [DeviceTrackerType.WAREHOUSE]: i18next.t('devices:locationType.WAREHOUSE'),
    [DeviceTrackerType.INSTALLATION]: i18next.t('devices:locationType.INSTALLATION'),
    [DeviceTrackerType.OUT_OF_SERVICE]: i18next.t('devices:locationType.OUT_OF_SERVICE'),
  };
  return labels[type] ?? i18next.t('devices:locationType.UNSPECIFIED');
};
const STATE_PREFIX = 'installation-location:installationLocationDetail.installationState';

export const installationStateLabel = (state: InstallationState | null): string => {
  if (!state) return i18next.t(`${STATE_PREFIX}.unspecified`);
  switch (state.state) {
    case InstallationStateType.OK:
      return i18next.t(`${STATE_PREFIX}.ok`);
    case InstallationStateType.URGENT:
      return i18next.t(`${STATE_PREFIX}.urgent`);
    case InstallationStateType.IN_MAINTENANCE:
      return i18next.t(`${STATE_PREFIX}.maintenance`);
    case InstallationStateType.PUBLIC_WATER_INVASION:
      return i18next.t(`${STATE_PREFIX}.invasion`);
    case InstallationStateType.ERROR:
      return i18next.t(`${STATE_PREFIX}.error`);
    case InstallationStateType.CUSTOM:
      return state.customName;
    default:
      return i18next.t(`${STATE_PREFIX}.unspecified`);
  }
};
